import { tryOnUnmounted } from '@vueuse/core';
import Big from 'big.js';
import {
  camelCase,
  cloneDeep as _cloneDeep,
  debounce,
  get,
  intersectionWith,
  isEqual,
  isPlainObject,
  memoize,
  mergeWith,
  unionWith,
} from 'lodash-es';
import { unref } from 'vue';
import type { Recordable, TargetContext, TimeoutHandle } from '@mts-fe/types';
import type { DebouncedFunc } from 'lodash-es';
import type { App, Component } from 'vue';
import { isArray, isObject } from './is';

export const SYS_PREFIX = 'mts';

/**
 * Convert a hyphen-delimited string to camelCase.
 */
export const camelize: (str: string) => string = memoize(camelCase);

/**
 * Creates an object composed of the picked object properties.
 * @returns — Returns the new object.
 */
export const pick = <T extends Recordable>(obj: T, keys: string[]) => {
  if (!obj) return {} as T;

  const result = {};
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result as T;
};

const hasOwn = Object.prototype.hasOwnProperty;
export const omit = <T extends Recordable, K extends string | number | symbol>(
  obj: T | undefined,
  keys: readonly K[] | K[],
) => {
  if (!obj) return {} as Partial<T>;

  const keysLookup = new Set<K>(keys);

  const result: Recordable = {};
  for (const key in obj) {
    if (hasOwn.call(obj, key) && !keysLookup.has(key as any)) {
      result[key] = obj[key];
    }
  }
  return result as Partial<T>;
};

/**
 * 获取基础容器元素
 * @returns 返回全屏元素或body元素
 */
export const getBaseContainer = (): HTMLElement =>
  document.fullscreenElement ? (document.fullscreenElement as HTMLElement) : document.body;

/**
 * @description: 设置UI挂载节点
 * @param node 指定节点，默认为null
 * @returns 返回父节点或基础容器
 */
export const getPopupContainer = (node?: HTMLElement): HTMLElement =>
  (node?.parentNode as HTMLElement) ?? getBaseContainer();

/** 将字符串转换成合法数值 */
export function formatNumber(value: string): string {
  let result = '';
  let hasDot = false;
  let hasNegative = false;
  let negativeIndex = -1;

  for (let i = 0; i < value.length; i++) {
    const char = value[i];

    if (char === '-') {
      if (i === 0 && !hasNegative) {
        hasNegative = true;
        negativeIndex = 0;
        result += char;
      }
      continue;
    }

    if (char === '.') {
      if (!hasDot) {
        hasDot = true;
        result += char;
      }
      continue;
    }

    if (char >= '0' && char <= '9') {
      result += char;
      continue;
    }
  }

  if (negativeIndex !== -1 && negativeIndex !== 0) {
    result = result.replace('-', '');
  }
  return result;
}

/**
 * 对数组指定字段进行数值求和
 * @param list 数据列表
 * @param iteratee 求和字段名或自定义计算函数
 * @param accumulator 初始累加值，默认为0
 * @returns 求和结果
 */
export const getNumberTotal = (
  list: Recordable[],
  iteratee: string | ((record: Recordable, index: number, list: Recordable[]) => number | Big),
  accumulator = 0,
) => {
  if (!isArray(list) || list.length === 0) {
    return accumulator;
  }

  try {
    if (typeof iteratee === 'string') {
      const field = iteratee;
      let sum = Big(accumulator);

      for (let i = 0; i < list.length; i++) {
        const value = iteratee.includes('.') ? get(list[i], iteratee) : list[i]?.[field];
        if (value !== undefined && value !== null) {
          sum = sum.plus(Big(value));
        }
      }
      return sum.toNumber();
    }

    // For custom iteratee function
    let sum = Big(accumulator);
    for (let i = 0; i < list.length; i++) {
      const result = iteratee(list[i], i, list);
      if (result !== undefined && result !== null) {
        sum = sum.plus(Big(result));
      }
    }
    return sum.toNumber();
  } catch (error) {
    console.error('getNumberTotal error:', error, list);
  }
};

/**
 * 动态获取组件props
 * @param props 组件props对象
 * @returns 解构后的props对象
 */
export function getDynamicProps<T extends Record<string, unknown>, U>(props: T): Partial<U> {
  const ret: Recordable = {};

  Object.keys(props).forEach((key) => {
    ret[key] = unref((props as Recordable)[key]);
  });

  return ret as Partial<U>;
}

// https://github.com/vant-ui/vant/issues/8302
interface EventShim {
  new (...args: any[]): {
    $props: {
      onClick?: (...args: any[]) => void;
    };
  };
}

export type WithInstall<T> = T & { install(app: App): void } & EventShim;
export type CustomComponent = Component & { displayName?: string };

/**
 * 为组件添加安装方法
 * @param component 组件对象
 * @param alias 组件别名
 * @returns 带安装方法的组件
 */
export const withInstall = <T extends CustomComponent>(component: T, alias?: string) => {
  (component as Record<string, unknown>).install = (app: App) => {
    const compName = component.name || component.displayName;
    if (!compName) return;
    app.component(compName, component);
    if (alias) {
      app.config.globalProperties[alias] = component;
    }
  };
  return component as WithInstall<T>;
};

/**
 * 按顺序从多个对象中查询指定 key 的值，
 * @returns {*} 返回第一个匹配到的非 undefined 值
 */
export function findFirstDefinedValue(objects: Recordable[], keys: string[]) {
  for (const obj of objects) {
    if (obj === null || obj === void 0) continue;

    for (const key of keys) {
      const value = obj[key];
      if (value !== void 0) {
        return value;
      }
    }
  }
}

/**
 * 分组并去重方案
 * @param {Array} dataSource - 原始数据
 * @param {string} groupKey - 分组字段
 * @param {string} uniqueKey - 去重依据字段（可选）
 * @returns {Object} 去重后的分组对象
 */
export function groupAndUniqueData<T extends Recordable>(
  dataSource: T[],
  groupKey: string,
  uniqueKey?: string,
) {
  const grouped = new Map<string, T[]>();

  for (const item of dataSource) {
    const groupValue = item[groupKey];
    if (!grouped.has(groupValue)) {
      grouped.set(groupValue, []);
    }

    const group = grouped.get(groupValue)!;
    if (uniqueKey) {
      if (!group.some((existing) => existing[uniqueKey] === item[uniqueKey])) {
        group.push(item);
      }
    } else {
      if (!group.includes(item)) {
        group.push(item);
      }
    }
  }

  return Object.fromEntries(grouped);
}

export function composeBeforeFetch<T = any>(
  beforeFn: (params: T) => T,
  nextFn?: (params: T) => T | false,
): (params: T) => T | false {
  if (!nextFn) return beforeFn;
  return (params: T) => nextFn(beforeFn(params));
}

function isPureJSON(obj) {
  if (obj === null || typeof obj !== 'object') {
    return true;
  }

  if (isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      if (!isPureJSON(obj[i])) {
        return false;
      }
    }
    return true;
  }

  for (const key in obj) {
    if (!hasOwn.call(obj, key)) continue;

    const value = obj[key];
    const type = typeof value;

    if (
      type === 'symbol' ||
      type === 'function' ||
      (type === 'object' && !isArray(value) && !isPlainObject(value))
    ) {
      return false;
    }

    if (type === 'object' && value !== null) {
      if (!isPureJSON(value)) {
        return false;
      }
    }
  }

  return true;
}
/** 深拷贝：仅复制简单对象，其他保留原始引用 */
function fastCloneDeep(source: unknown, hash = new WeakMap()) {
  if (source === null || typeof source !== 'object') return source;

  if (hash.has(source)) return hash.get(source);

  if (isArray(source)) {
    const result = new Array(source.length);
    hash.set(source, result);
    for (let i = 0; i < source.length; i++) {
      result[i] = fastCloneDeep(source[i], hash);
    }
    return result;
  }

  if (!isPlainObject(source)) return source;

  const result = Object.create(Object.getPrototypeOf(source));
  hash.set(source, result);

  const keys = Object.keys(source);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    result[key] = fastCloneDeep(source[key], hash);
  }

  const symbolKeys = Object.getOwnPropertySymbols(source);
  for (let i = 0; i < symbolKeys.length; i++) {
    const key = symbolKeys[i];
    result[key] = fastCloneDeep(source[key], hash);
  }

  return result;
}

/**
 * 复制对象
 * @param value 要复制的对象
 * @param mode 复制方式，json lodash 自动fast
 */
export const cloneDeep = <T>(value: T, mode: 'json' | 'auto' | '_' = 'auto'): T => {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (mode === 'json') {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (error) {
      console.warn('JSON.parse(JSON.stringify()) failed, falling back to lodash copy:', error);
    }
  }

  if (mode === '_') {
    return _cloneDeep(value);
  }

  return isPureJSON(value) ? cloneDeep(value, 'json') : fastCloneDeep(value);
};

/**
 * Recursively merge two objects.
 * 递归合并两个对象。
 *
 * @param target The target object to merge into. 目标对象，合并后结果存放于此。
 * @param source The source object to merge from. 要合并的源对象。
 * @param options.mergeArrays How to merge arrays. Default is "replace".
 *        如何合并数组。默认为replace。
 *        - "union": Union the arrays. 对数组执行并集操作。
 *        - "intersection": Intersect the arrays. 对数组执行交集操作。
 *        - "concat": Concatenate the arrays. 连接数组。
 *        - "replace": Replace the source array with the target array. 用目标数组替换源数组。
 * @param options.mergeUndefined 允许空值assign原字段(需要key存在)
 * @param options.omitNull 只assign有效的值，忽略null
 * @returns The merged object. 合并后的对象。
 */
export function deepMerge<T extends object | null | undefined, U extends object | null | undefined>(
  target: T,
  source: U,
  options: {
    mergeArrays?: 'union' | 'intersection' | 'concat' | 'replace';
    mergeUndefined?: boolean;
    omitNull?: boolean;
  } = {},
): T & U {
  if (!target) {
    return source as T & U;
  }
  if (!source) {
    return (options.mergeUndefined ? source : target) as T & U;
  }

  return mergeWith({}, target, source, (objValue, srcValue, key, object) => {
    // 数组合并处理
    if (isArray(srcValue) && isArray(objValue)) {
      const { mergeArrays = 'replace' } = options;
      switch (mergeArrays) {
        case 'union':
          return unionWith(objValue, srcValue, isEqual);
        case 'intersection':
          return intersectionWith(objValue, srcValue, isEqual);
        case 'concat':
          return objValue.concat(srcValue);
        case 'replace':
          return srcValue;
        default:
          throw new Error(`Unknown merge array strategy: ${mergeArrays as string}`);
      }
    }

    // 对象递归合并
    if (isObject(srcValue) && isObject(objValue)) {
      return deepMerge(objValue, srcValue, options);
    }

    // 处理undefined值
    if (options.mergeUndefined && objValue !== srcValue && typeof srcValue === 'undefined') {
      object[key] = srcValue;
    }

    // 忽略null值
    if (options.omitNull && srcValue === null) {
      return objValue;
    }

    return undefined;
  });
}

/**
 * 打开新窗口
 * @param url 窗口地址
 * @param opt 窗口配置选项
 * @returns 新窗口对象或null
 */
export function openWindow(
  url: string,
  opt?: {
    target?: TargetContext | string;
    noopener?: boolean;
    noreferrer?: boolean;
    innerWidth?: number;
    innerHeight?: number;
  },
) {
  const {
    target = '__blank',
    noopener = true,
    noreferrer = true,
    innerWidth,
    innerHeight,
  } = opt || {};
  const feature: string[] = [];

  noopener && feature.push('noopener=yes');
  noreferrer && feature.push('noreferrer=yes');
  innerWidth && feature.push('innerWidth=' + innerWidth);
  innerHeight && feature.push('innerHeight=' + innerHeight);

  const newWin = window.open(url, target, feature.join(','));
  if (!newWin) {
    console.warn('新窗口打开可能被浏览器拦截');
    return newWin;
  }

  newWin.focus();
  newWin.onload = function () {
    newWin.focus();
  };
  return newWin;
}

/**
 * 递归 setTimeout 封装
 * @param fn 执行函数（返回 `false` 可终止循环）
 * @param interval 间隔时间（毫秒）
 * @param options 配置项（立即执行、错误处理等）
 * @returns 控制器对象（可手动停止）
 */
export function recursiveTimeout<T extends any[]>(
  fn: (...args: T) => boolean | void | Promise<boolean | void>,
  interval: number,
  options?: {
    immediate?: boolean; // 是否立即执行首次
    args?: T; // 传递给 fn 的参数
    onError?: (err: Error) => void; // 错误捕获
  },
): { stop: () => void } {
  let timer: TimeoutHandle | null = null;
  let isActive = true;
  let isExecuting = false;

  const execute = async (): Promise<void> => {
    if (!isActive || isExecuting) return;

    isExecuting = true;

    try {
      const shouldContinue = await fn(...(options?.args || ([] as unknown as T)));
      if (shouldContinue === false) {
        stop();
        return;
      }
    } catch (err) {
      options?.onError?.(err as Error);
    } finally {
      isExecuting = false;
    }

    if (isActive) {
      timer = setTimeout(execute, interval);
    }
  };

  const stop = (): void => {
    isActive = false;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  // 立即执行或延迟执行
  if (options?.immediate) {
    execute().catch((err) => {
      options?.onError?.(err as Error);
    });
  } else {
    timer = setTimeout(execute, interval);
  }

  tryOnUnmounted(stop);

  return { stop };
}

export function rafTimeout(callback: () => void, delay: number) {
  const startTime = performance.now();
  let requestId: number;

  function checkTime(currentTime) {
    const elapsed = currentTime - startTime;

    if (elapsed >= delay) {
      callback();
      cancelAnimationFrame(requestId);
    } else {
      requestId = requestAnimationFrame(checkTime);
    }
  }

  requestId = requestAnimationFrame(checkTime);

  return () => cancelAnimationFrame(requestId);
}
/**
 * 用户交互检测器

 * 用于检测页面上的用户交互活动，并在指定时间阈值（默认1分钟）内仅执行最后一次回调
 * @example
 * ```typescript
 * const detector = new UserInteractionDetector(() => {
 *   console.log('用户有交互活动');
 * });
 *
 * // 使用完毕后清理
 * detector.destroy();
 * ```
 */
export class UserInteractionDetector {
  private eventListeners: Map<string, (event: Event) => void>;
  private debouncedHandler: DebouncedFunc<() => void>;

  constructor(callback: () => void, maxWait = 60000) {
    this.eventListeners = new Map();
    this.debouncedHandler = debounce(
      () => {
        try {
          callback();
        } catch (error) {
          console.error('Error in interaction callback:', error);
        }
      },
      maxWait,
      { leading: false, trailing: true, maxWait },
    );

    this.setupListeners();
  }

  private setupListeners(events = ['click', 'keydown', 'scroll']) {
    events.forEach((event) => {
      document.addEventListener(event, this.debouncedHandler, { passive: true, capture: true });
      this.eventListeners.set(event, this.debouncedHandler);
    });
  }

  /** 手动触发立即执行 */
  triggerImmediately() {
    this.debouncedHandler.flush();
  }

  destroy() {
    this.eventListeners.forEach((handler, event) =>
      document.removeEventListener(event, handler, { capture: true }),
    );
    this.eventListeners.clear();

    this.debouncedHandler?.cancel();
    this.debouncedHandler = null as any;
  }
}
